// Cloud speech-to-text for VR voice navigation (push-to-talk).
//
// Why this exists alongside src/ui/voiceInput.ts:
//   voiceInput.ts wraps the Web Speech API, which the Meta Quest Browser
//   does NOT support — so on-headset voice commands can't use it. This
//   module records mic audio with MediaRecorder and POSTs it to
//   /api/chat (`action:'transcribe'`), which forwards to Groq Whisper.
//   Works in any Chromium-based XR browser (Quest, Pico, Wolvic).
//
// Push-to-talk shape: start() on controller grip-down, stop() on grip-up.
// The public surface (isSupported / prewarm / start / stop / cancel) is
// vendor-neutral so the transport can be swapped without touching callers.
//
// Mic permission on Quest: getUserMedia must be granted once. prewarm()
// is best-effort and idempotent — App calls it on VR enter so the first
// grip-to-talk has no setup delay, and the granted permission persists
// across sessions on the headset.

export class CloudVoiceInput {
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private mimeType = '';

  /** Browser can capture + encode mic audio? (Chromium yes; old iOS Safari no.) */
  static isSupported(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function' &&
      typeof MediaRecorder !== 'undefined'
    );
  }

  /**
   * Acquire the mic ahead of time. Best-effort and idempotent — safe to call
   * on every VR enter. Resolves true once the stream is live (or already was).
   */
  async prewarm(): Promise<boolean> {
    if (this.stream) return true;
    if (!CloudVoiceInput.isSupported()) return false;
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      return true;
    } catch (err) {
      console.warn('[cloudVoice] mic permission/prewarm failed', err);
      return false;
    }
  }

  isRecording(): boolean {
    return this.recorder?.state === 'recording';
  }

  /** Begin capturing. Resolves true once recording actually started. */
  async start(): Promise<boolean> {
    if (this.isRecording()) return true;
    const ready = await this.prewarm();
    if (!ready || !this.stream) return false;

    const preferred = pickMimeType();
    try {
      this.recorder = preferred
        ? new MediaRecorder(this.stream, { mimeType: preferred })
        : new MediaRecorder(this.stream);
    } catch {
      this.recorder = new MediaRecorder(this.stream);
    }
    this.mimeType = this.recorder.mimeType || preferred || 'audio/webm';
    this.chunks = [];
    this.recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };
    try {
      this.recorder.start();
    } catch (err) {
      console.warn('[cloudVoice] recorder.start failed', err);
      this.recorder = null;
      return false;
    }
    return true;
  }

  /**
   * Stop capturing, transcribe via /api/chat, and resolve the recognized text
   * (or null on silence / error). Safe to call when not recording (→ null).
   */
  async stop(): Promise<string | null> {
    const rec = this.recorder;
    if (!rec || rec.state === 'inactive') {
      this.recorder = null;
      return null;
    }
    const blob = await new Promise<Blob>((resolve) => {
      rec.onstop = () => resolve(new Blob(this.chunks, { type: this.mimeType }));
      try {
        rec.stop();
      } catch {
        resolve(new Blob(this.chunks, { type: this.mimeType }));
      }
    });
    this.recorder = null;
    this.chunks = [];
    if (blob.size === 0) return null;
    return this.transcribe(blob);
  }

  /** Discard any in-flight recording without transcribing. Idempotent. */
  cancel(): void {
    if (this.recorder && this.recorder.state !== 'inactive') {
      try {
        this.recorder.stop();
      } catch {
        /* ignore */
      }
    }
    this.recorder = null;
    this.chunks = [];
  }

  /** Release the mic stream entirely. Call on teardown. */
  release(): void {
    this.cancel();
    if (this.stream) {
      for (const track of this.stream.getTracks()) track.stop();
      this.stream = null;
    }
  }

  private async transcribe(blob: Blob): Promise<string | null> {
    try {
      const audioBase64 = await blobToBase64(blob);
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          action: 'transcribe',
          audioBase64,
          mimeType: blob.type || this.mimeType,
          // Detect, do not pin. A hardcoded 'pt' made an English utterance
          // come back as Portuguese-shaped nonsense — not merely a worse
          // transcript, but one no command matcher could ever hit. A headset
          // visitor cannot change a language setting mid-tour, so both
          // languages have to work in the same session without one.
          lang: 'auto',
        }),
      });
      if (!res.ok) {
        console.warn('[cloudVoice] transcribe HTTP', res.status);
        return null;
      }
      const body = (await res.json()) as { text?: string };
      return body.text?.trim() || null;
    } catch (err) {
      console.warn('[cloudVoice] transcribe failed', err);
      return null;
    }
  }
}

/** First MediaRecorder mimeType the browser supports, or '' to let it choose. */
function pickMimeType(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
  if (typeof MediaRecorder !== 'undefined' && typeof MediaRecorder.isTypeSupported === 'function') {
    for (const c of candidates) {
      if (MediaRecorder.isTypeSupported(c)) return c;
    }
  }
  return '';
}

/** Read a Blob as bare base64 (strips the `data:...;base64,` prefix). */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      } else {
        reject(new Error('FileReader returned non-string result'));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
    reader.readAsDataURL(blob);
  });
}
